import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const CONTROLLER_SOURCE = 'crafty-cart-rewards';
const REQUEST_TIMEOUT = 8000;
const CART_CACHE_TTL = 15000;
const DRAWER_SETTLE_DELAY = 260;

class CraftyCartRewards {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.giftVariantIds = (element.dataset.giftVariants || '')
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);
    this.queue = Promise.resolve();
    this.timer = null;
    this.forceFreshOnNextSync = false;
    this.lastCart = null;
    this.lastSyncAt = 0;
    this.updatingMessage = element.dataset.rewardsUpdating || 'Updating rewards…';

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('click', this.onCartTrigger, true);
    document.addEventListener('pointerenter', this.onCartIntent, true);
    document.addEventListener('submit', this.onUpsellSubmit);
  }

  onCartUpdate = (event) => {
    if (event.detail?.data?.source === CONTROLLER_SOURCE) {
      window.setTimeout(syncAllCarousels, 80);
      return;
    }
    if (event.detail?.data?.didError) return;

    // Product forms dispatch cart:add with a partial resource, but their section
    // HTML contains the authoritative reward state. Avoid a duplicate cart fetch
    // when the product and its gift were already added together.
    if (event.detail?.eventType === 'cart:add') {
      this.lastCart = null;
      this.lastSyncAt = 0;
      const sectionHtml = event.detail?.data?.sections?.[this.sectionId];
      if (!this.sectionHasConsistentRewards(sectionHtml)) {
        this.schedule(null, { forceFresh: true, delay: DRAWER_SETTLE_DELAY });
      }
      window.setTimeout(syncAllCarousels, 80);
      return;
    }

    const resource = event.detail?.resource;
    if (resource?.items) {
      this.lastCart = resource;
      this.lastSyncAt = Date.now();
    }
    this.schedule(resource);
    window.setTimeout(syncAllCarousels, 80);
  };

  onCartTrigger = (event) => {
    if (!event.target.closest?.('[data-testid="cart-drawer-trigger"]')) return;
    this.prepareDrawerImages();
    if (this.currentRewardsAreConsistent()) return;

    const recentCart = Date.now() - this.lastSyncAt < CART_CACHE_TTL ? this.lastCart : null;
    this.schedule(recentCart, {
      forceFresh: !recentCart,
      delay: DRAWER_SETTLE_DELAY,
    });
  };

  onCartIntent = (event) => {
    if (!event.target.closest?.('[data-testid="cart-drawer-trigger"]')) return;
    this.prepareDrawerImages();
  };

  onUpsellSubmit = (event) => {
    const form = event.target.closest?.('[data-crafty-cart-upsell-form]');
    if (!form) return;

    event.preventDefault();
    this.queue = this.queue
      .then(() => this.addUpsell(form))
      .catch((error) => console.error('Cart accessory add failed', error));
  };

  schedule(resource, { forceFresh = false, delay = 40 } = {}) {
    if (forceFresh) this.forceFreshOnNextSync = true;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      const shouldForceFresh = this.forceFreshOnNextSync;
      this.forceFreshOnNextSync = false;
      this.queue = this.queue
        .then(() => this.reconcile(resource, { forceFresh: shouldForceFresh }))
        .catch((error) => console.error('Cart reward sync failed', error));
    }, delay);
  }

  currentRewardsAreConsistent() {
    return document.querySelector('[data-crafty-rewards]')?.dataset.rewardsConsistent === 'true';
  }

  sectionHasConsistentRewards(sectionHtml) {
    if (!sectionHtml) return false;
    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return html.querySelector('[data-crafty-rewards]')?.dataset.rewardsConsistent === 'true';
  }

  prepareDrawerImages() {
    const images = document.querySelectorAll(
      '.cart-drawer__dialog .crafty-cart-rewards img[loading="lazy"], ' +
      '.cart-drawer__dialog .crafty-cart-item:first-child img[loading="lazy"]'
    );
    images.forEach((image) => {
      image.loading = 'eager';
    });
  }

  async fetchCart() {
    return this.fetchJSON((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
  }

  async fetchJSON(path, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    let response;
    try {
      response = await fetch(path, { ...options, signal: controller.signal });
    } finally {
      window.clearTimeout(timeout);
    }

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  getPlan(cart) {
    return {
      removeKeys: cart.items.filter(item => item.properties?._GIFT_PRODUCT === 'true' && item.final_line_price === 0).map(item => item.key),
      addIds: [],
    };
  }

  async request(path, body) {
    return this.fetchJSON((window.Shopify?.routes?.root || '/') + path.replace(/^\//, ''), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...body,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      }),
    });
  }

  async reconcile(resource, { forceFresh = false } = {}) {
    if (!this.sectionId) return;

    let isMutating = false;

    try {
      const cachedCart = Date.now() - this.lastSyncAt < CART_CACHE_TTL ? this.lastCart : null;
      const cart = forceFresh
        ? await this.fetchCart()
        : resource?.items
          ? resource
          : cachedCart || (await this.fetchCart());
      this.lastCart = cart;
      this.lastSyncAt = Date.now();

      const plan = this.getPlan(cart);
      if (plan.removeKeys.length === 0 && plan.addIds.length === 0) return;

      isMutating = true;
      this.setRewardBusy(true);
      let sectionResponse = null;

      if (plan.removeKeys.length > 0) {
        const updates = Object.fromEntries(plan.removeKeys.map((key) => [key, 0]));
        sectionResponse = await this.request('/cart/update.js', { updates });
      }

      if (plan.addIds.length > 0 && plan.parentKey) {
        const items = plan.addIds.map((id) => ({
          id,
          quantity: 1,
          properties: { _GIFT_PRODUCT: 'true' },
          parent_line_key: plan.parentKey,
        }));
        sectionResponse = await this.request('/cart/add.js', { items });
      }

      const finalCart = plan.addIds.length > 0
        ? this.buildCartSnapshot(cart, plan)
        : sectionResponse?.items ? sectionResponse : cart;
      await this.renderCart(finalCart, sectionResponse);
    } finally {
      if (isMutating) this.setRewardBusy(false);
    }
  }

  setRewardBusy(isBusy) {
    const drawer = document.querySelector('.cart-drawer__dialog');
    if (!drawer) return;

    drawer.toggleAttribute('data-rewards-busy', isBusy);
    const rewards = drawer.querySelector('.crafty-cart-rewards');
    const status = drawer.querySelector('[data-cart-reward-status]');
    const checkout = drawer.querySelector('#checkout');

    if (isBusy) {
      rewards?.setAttribute('aria-busy', 'true');
      if (status) status.textContent = this.updatingMessage;
      if (checkout) {
        checkout.dataset.rewardsDisabled = 'true';
        checkout.disabled = true;
      }
      return;
    }

    rewards?.removeAttribute('aria-busy');
    if (status) status.textContent = '';
    if (checkout?.dataset.rewardsDisabled === 'true') {
      checkout.disabled = false;
      delete checkout.dataset.rewardsDisabled;
    }
  }

  buildCartSnapshot(cart, plan) {
    const removedKeys = new Set(plan.removeKeys);
    const removedQuantity = cart.items
      .filter((item) => removedKeys.has(item.key))
      .reduce((total, item) => total + item.quantity, 0);
    const retainedItems = cart.items.filter((item) => !removedKeys.has(item.key));
    const addedItems = plan.addIds.map((id) => ({
      id,
      variant_id: id,
      quantity: 1,
      properties: { _GIFT_PRODUCT: 'true' },
    }));

    return {
      ...cart,
      item_count: Math.max(0, (cart.item_count || 0) - removedQuantity + addedItems.length),
      items: [...retainedItems, ...addedItems],
    };
  }

  async addUpsell(form) {
    const button = form.querySelector('button[type="submit"]');
    const liveRegion = form.querySelector('[role="status"]');
    const variantId = Number(new FormData(form).get('id'));
    if (!variantId || button?.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const sectionResponse = await this.request('/cart/add.js', {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { _NTFG: 'true' },
          },
        ],
      });
      const finalCart = await this.fetchCart();
      await this.renderCart(finalCart, sectionResponse);
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      liveRegion?.classList.remove('visually-hidden');
      if (liveRegion) liveRegion.textContent = window.craftyThemeI18n?.add_error || 'Could not add this item. Please try again.';
      throw error;
    }
  }

  async renderCart(cart, sectionResponse) {
    this.lastCart = cart;
    this.lastSyncAt = Date.now();
    const sectionHtml = sectionResponse?.sections?.[this.sectionId];
    if (sectionHtml) {
      await morphSection(this.sectionId, sectionHtml, 'hydration');
    }

    const eventTarget = document.querySelector('cart-items-component[data-drawer]') || document;
    eventTarget.dispatchEvent(
      new CartUpdateEvent(cart, CONTROLLER_SOURCE, {
        source: CONTROLLER_SOURCE,
        sections: sectionResponse?.sections || {},
      })
    );
  }
}

function initializeRewards() {
  const element = document.querySelector('[data-crafty-rewards]');
  if (!element || window.craftyCartRewards) return;
  window.craftyCartRewards = new CraftyCartRewards(element);
}

function syncCarousel(track) {
  const viewport = track.closest('.crafty-cart-upsells__viewport');
  const previous = viewport?.querySelector('[data-cart-upsell-previous]');
  const next = viewport?.querySelector('[data-cart-upsell-next]');
  if (!previous || !next) return;

  const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
  previous.hidden = track.scrollLeft <= 4;
  next.hidden = track.scrollLeft >= maxScroll - 4 || maxScroll <= 4;
}

function syncAllCarousels() {
  document.querySelectorAll('[data-cart-upsell-track]').forEach(syncCarousel);
}

document.addEventListener('click', (event) => {
  const previous = event.target.closest('[data-cart-upsell-previous]');
  const next = event.target.closest('[data-cart-upsell-next]');
  const button = previous || next;
  if (!button) return;

  const track = button.closest('.crafty-cart-upsells__viewport')?.querySelector('[data-cart-upsell-track]');
  if (!track) return;

  const card = track.querySelector('.crafty-cart-upsell');
  const distance = card ? card.getBoundingClientRect().width + 12 : track.clientWidth * 0.8;
  track.scrollBy({
    left: previous ? -distance : distance,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });
  window.setTimeout(() => syncCarousel(track), 320);
});

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches('[data-cart-upsell-track]')) {
      window.requestAnimationFrame(() => syncCarousel(event.target));
    }
  },
  true
);

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeRewards();
      syncAllCarousels();
    },
    { once: true }
  );
} else {
  initializeRewards();
  syncAllCarousels();
}
